import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ListChecks, Loader2, Plus, RefreshCw, Settings2, Trash2, Zap } from 'lucide-react';
import { adminAPI } from '../../services/adminApi';
import { formatMoney } from '../../utils/formatMoney';
import ConfirmModal from '../shared/ConfirmModal';
import { useToast } from '../shared/Toast';
import { LoadFailed } from '../settings/instantSettings';
import { SheetField, SheetLocked, SheetSection } from '../settings/sheet';
import { fmtDMY, fmtNum } from '../home/format';
// Προεπιλογή «σήμερα» στις φόρμες πληρωμής/εξοπλισμού: ώρα Ελλάδας (ως 24/10/2026 UTC — «χθες» ως τις 03:00).
import { athensYmd } from '../../utils/athensDate';
import {
  ASIDE_BTN, Column, DataTable, DetailPanel, EmptyState, GHOST_BTN, ICON_BTN, INPUT_CLS, ListPage, ListToolbar, PRIMARY_BTN,
  SELECT_CLS, Stat, Stats, StatusChip, ChipTone, matchesSearch,
} from '../shared/listPage';

/**
 * Superadmin «Τιμολόγηση» — mini CRM ανά κατάστημα:
 * χρεώσιμες λειτουργίες → προτεινόμενη τιμή, συμφωνημένη χρέωση + κύκλος,
 * ημερολόγιο περιόδων πληρωμής (από την εγκατάσταση — κλικ για πληρωμή/
 * αναίρεση, η «επόμενη πληρωμή» υπολογίζεται από αυτό), πακέτο παραστατικών
 * παρόχου (τιμολόγια ×2), πωλήσεις hardware/αποπληρωμή και σχόλια.
 *
 * Διάταξη «Πίνακας» (14/10/2026): ListPage + σύνολα ως απλό κείμενο + DataTable· η καρτέλα του καταστήματος
 * και ο κατάλογος «Λειτουργίες & Τιμές» σε συρτάρι δεξιά. Ίδια endpoints, ίδια σώματα, ίδιοι έλεγχοι με πριν
 * (adminApi: billing/overview, billing/store/:id, …/features, …/payments, …/hardware, billing/features). Οι
 * ερωτήσεις του browser (confirm/prompt/alert) έγιναν ConfirmModal / πεδίο στη θέση τους / Toast.
 * Η επαναφόρτωση της λίστας μετά από πληρωμή/hardware ΔΕΝ κλείνει πια την καρτέλα: ως 14/10 ξαναχτιζόταν
 * από τον server και χάνονταν οι αναποθήκευτες αλλαγές της φόρμας (βλ. και refreshLists).
 *
 * Η addMonthsClamped εδώ πρέπει να μένει ΠΑΝΟΜΟΙΟΤΥΠΗ με του backend
 * (services/storeBilling.js) — αλλιώς τα period_start δεν θα δένουν.
 *
 * Από 27/10/2026 (απόφαση χρήστη): το support κάνει ΟΛΑ όσα αφορούν ένα κατάστημα — μόνο ο κατάλογος «Λειτουργίες &
 * Τιμές» μένει στον super admin. «Καταγραφή από» (tracking_from): οι περίοδοι που ξεκινούν πριν από αυτήν φαίνονται
 * «πριν την καταγραφή», όχι ως χρέος. «Μαζική συμπλήρωση»: κύκλος και τιμή όλων των καταστημάτων σε έναν πίνακα.
 */

interface FeatureCatalogItem { id: number; code: string; name: string; monthly_price: string | number; is_active: boolean; display_order: number; }
interface OverviewStore {
  id: number; name: string; display_name?: string | null; organization_name?: string | null; is_active: boolean;
  billing: any | null;
  // Λειτουργίες που ανιχνεύτηκαν αυτόματα από τα ΠΡΑΓΜΑΤΙΚΑ flags του καταστήματος
  detected_codes: string[];
  features: { feature_id: number; name: string; price: number }[];
  suggested_monthly: number;
  suggested_cycle_price: number;
  docs_used: number; docs_raw: number; last_doc_at: string | null;
  hw_total: number; hw_paid: number; hw_items: number;
  last_payment: { paid_at: string; amount: string } | null;
}

type LoadState = 'ready' | 'error' | 'forbidden';

const CYCLES: { value: string; months: number }[] = [
  { value: 'monthly', months: 1 },
  { value: 'bimonthly', months: 2 },
  { value: 'quarterly', months: 3 },
  { value: 'semiannual', months: 6 },
  { value: 'annual', months: 12 },
];
const cycleMonths = (v?: string) => CYCLES.find(c => c.value === v)?.months || 1;

// Ποσό όπως πριν: ό,τι δεν διαβάζεται ως αριθμός = «—».
const money = (v: any) => {
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? formatMoney(n) : '—';
};
// Οι ημερομηνίες του billing είναι DATE («YYYY-MM-DD», config/database.js): ημερολογιακές, χωρίς ζώνη ώρας.
const dstr = (v?: string | null) => (v ? String(v).slice(0, 10) : '');
const dfmt = (v?: string | null) => (v ? fmtDMY(String(v).slice(0, 10)) : '—');

// Ημέρες μέχρι την επόμενη πληρωμή (αρνητικό = έληξε)
const daysUntil = (date?: string | null): number | null => {
  if (!date) return null;
  const due = new Date(String(date).slice(0, 10) + 'T00:00:00');
  return Math.round((due.getTime() - new Date(new Date().toDateString()).getTime()) / 864e5);
};

// Πρόσθεση μηνών σε 'YYYY-MM-DD' με clamping ημέρας (31 Ιαν + 1μ → 28/29 Φεβ).
// ΙΔΙΑ λογική με το backend addMonthsClamped (adminBilling.js) — κράτα σε συμφωνία.
const addMonthsClamped = (dateStr: string, months: number): string => {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  const total = (m - 1) + months;
  const ny = y + Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const daysInMonth = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${ny}-${String(nm).padStart(2, '0')}-${String(Math.min(d, daysInMonth)).padStart(2, '0')}`;
};

// Η ετικέτα της περιόδου ΓΡΑΦΕΤΑΙ στη βάση (period_covered της πληρωμής): μένει ελληνική και ίδια σε κάθε
// γλώσσα της οθόνης — γι' αυτό εδώ και όχι στο i18n.
const MONTHS_EL = ['Ιαν', 'Φεβ', 'Μάρ', 'Απρ', 'Μάι', 'Ιούν', 'Ιούλ', 'Αύγ', 'Σεπ', 'Οκτ', 'Νοέ', 'Δεκ'];

// «Ιούλ 26» για μηνιαίο, «Ιούν–Αύγ 26» για πολύμηνες περιόδους.
const periodLabel = (start: string, months: number): string => {
  const [y, m] = start.slice(0, 10).split('-').map(Number);
  const yy = (yr: number) => String(yr).slice(2);
  if (months === 1) return `${MONTHS_EL[m - 1]} ${yy(y)}`;
  const endIdx = (m - 1) + months - 1;
  const em = endIdx % 12;
  const ey = y + Math.floor(endIdx / 12);
  return ey === y
    ? `${MONTHS_EL[m - 1]}–${MONTHS_EL[em]} ${yy(y)}`
    : `${MONTHS_EL[m - 1]} ${yy(y)}–${MONTHS_EL[em]} ${yy(ey)}`;
};

const todayISO = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

interface BillingPeriod { start: string; end: string; label: string; status: 'paid' | 'overdue' | 'current' | 'future' | 'untracked'; paymentId?: number; }

// Παράγει τις περιόδους χρέωσης από την εγκατάσταση έως σήμερα+1 (ή έως τη
// λήξη σύμβασης όταν έχει οριστεί) και τις χαρακτηρίζει από τις πληρωμές.
// Απλήρωτη περίοδος που ξεκινά πριν από την «καταγραφή από» = «πριν την καταγραφή» (ίδιο με το nextDueFor του backend).
export const buildPeriods = (installedAt: string, cycle: string, contractEnd: string | null, payments: any[], trackingFrom: string | null = null): BillingPeriod[] => {
  const months = cycleMonths(cycle);
  const today = todayISO();
  const paidBy = new Map<string, number>();
  for (const p of payments) {
    if (p.period_start) paidBy.set(String(p.period_start).slice(0, 10), p.id);
  }
  const out: BillingPeriod[] = [];
  let start = installedAt.slice(0, 10);
  let guard = 0;
  while (guard++ < 120) {
    if (contractEnd && start >= contractEnd) break;
    const end = addMonthsClamped(start, months);
    const paymentId = paidBy.get(start);
    const status: BillingPeriod['status'] = paymentId != null ? 'paid'
      : trackingFrom && start < trackingFrom ? 'untracked'
      : end <= today ? 'overdue'
      : start <= today ? 'current'
      : 'future';
    out.push({ start, end, label: periodLabel(start, months), status, paymentId });
    // Χωρίς λήξη σύμβασης: έως και μία περίοδο μετά την τρέχουσα.
    if (!contractEnd && start > today) break;
    start = end;
  }
  return out;
};

const storeNameOf = (s: OverviewStore) => s.display_name || s.name;

const PERIOD_TONE: Record<BillingPeriod['status'], ChipTone> = { paid: 'ok', overdue: 'danger', current: 'warn', future: 'muted', untracked: 'muted' };
// Οι κουκκίδες του υπομνήματος στους τόνους των ετικετών (StatusChip). «Πριν την καταγραφή»: άδεια κουκκίδα, και η
// ετικέτα πιο αχνή από το «μελλοντικό».
const PERIOD_DOT: Record<BillingPeriod['status'], string> = {
  paid: 'bg-green-600 dark:bg-green-400', overdue: 'bg-red-600 dark:bg-red-400', current: 'bg-amber-500 dark:bg-amber-400', future: 'bg-t4',
  untracked: 'border border-t4',
};

// Χρώμα μόνο όταν κάτι θέλει προσοχή· αλλιώς απλό κείμενο.
const DANGER_INK = 'text-red-700 dark:text-red-400';
const WARN_INK = 'text-amber-700 dark:text-amber-400';
// Τα πεδία ημερομηνίας: το εικονίδιο του ημερολογίου να φαίνεται και στο σκούρο.
const DATE_CLS = `${INPUT_CLS} dark:[color-scheme:dark]`;
const ERROR_CLS = 'mt-1.5 text-xs text-red-600 dark:text-red-400';

/** Χρήση παραστατικών: «12.345 / 50.000» με λεπτή μπάρα (πορτοκαλί ≥75%, κόκκινη ≥90%). */
const DocsUsage: React.FC<{ store: OverviewStore }> = ({ store }) => {
  const { t } = useTranslation(['superadminOps']);
  const quota = store.billing?.provider_doc_quota;
  if (!quota) return <span className="text-xs text-t3">{t('superadminOps:billing.docsNoQuota', { used: fmtNum(store.docs_used) })}</span>;
  const pct = Math.min(100, Math.round((store.docs_used / quota) * 100));
  const ink = pct >= 90 ? `font-semibold ${DANGER_INK}` : pct >= 75 ? WARN_INK : 'text-t2';
  const bar = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-brand';
  return (
    <span className="block w-full max-w-[150px]" title={`${pct}%`}>
      <span className="flex justify-between gap-2 text-xs tabular-nums">
        <span className={ink}>{fmtNum(store.docs_used)}</span>
        <span className="text-t4">/ {fmtNum(quota)}</span>
      </span>
      <span className="mt-1 block h-1 rounded-full bg-surface-3 overflow-hidden" aria-hidden="true">
        <span className={`block h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
};

// support: χρησιμοποιείται και από το SupportPanel — ό,τι και ο super admin για κάθε κατάστημα (27/10/2026), χωρίς
// τον κατάλογο «Λειτουργίες & Τιμές». focusStore: «Άνοιγμα στις Χρεώσεις» από την κάρτα καταστήματος (nonce = κάθε
// πάτημα ξανανοίγει).
const SuperAdminBillingTab: React.FC<{ support?: boolean; focusStore?: { storeId: number; nonce: number } | null }> = ({
  support = false, focusStore = null,
}) => {
  const { t } = useTranslation(['superadminOps', 'common', 'listPage']);
  const [stores, setStores] = useState<OverviewStore[]>([]);
  const [catalog, setCatalog] = useState<FeatureCatalogItem[]>([]);
  const [detectableCodes, setDetectableCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<LoadState>('ready');
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [selId, setSelId] = useState<number | null>(null);
  const handledFocus = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d: any = await adminAPI.getBillingOverview();
      setStores(d.stores || []);
      setCatalog(d.feature_catalog || []);
      setDetectableCodes(d.detectable_codes || []);
      setState('ready');
      setLoaded(true);
    } catch (e: any) {
      console.error('Billing overview failed:', e);
      setState(e?.response?.status === 403 ? 'forbidden' : 'error');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!focusStore || !loaded || handledFocus.current === focusStore.nonce) return;
    handledFocus.current = focusStore.nonce;
    if (stores.some(s => s.id === focusStore.storeId)) setSelId(focusStore.storeId);
  }, [focusStore, loaded, stores]);

  const summary = useMemo(() => {
    let overdue = 0, dueSoon = 0, monthly = 0, hwOutstanding = 0;
    for (const s of stores) {
      if (!s.is_active) continue;
      // Χωρίς συμφωνημένη τιμή δεν υπάρχει χρέος (27/10/2026: η συμπλήρωση έδωσε εγκατάσταση σε δεκάδες καταστήματα
      // χωρίς τιμή — δεν μετρούν ως «ληγμένα» ώσπου να γραφτεί).
      const d = s.billing?.agreed_price != null ? daysUntil(s.billing?.next_due_date) : null;
      if (d !== null && d < 0) overdue++;
      else if (d !== null && d <= 15) dueSoon++;
      const agreed = parseFloat(s.billing?.agreed_price);
      if (Number.isFinite(agreed)) monthly += agreed / cycleMonths(s.billing?.billing_cycle);
      hwOutstanding += Math.max(0, s.hw_total - s.hw_paid);
    }
    return { overdue, dueSoon, monthly, hwOutstanding };
  }, [stores]);

  const cycleLabel = useCallback((v?: string) => (
    v && CYCLES.some(c => c.value === v) ? t(`superadminOps:billing.cycles.${v}`) : v || '—'
  ), [t]);

  const dueCell = useCallback((due?: string | null, priced = true) => {
    const d = daysUntil(due);
    if (d === null) return <span className="text-t4">—</span>;
    if (!priced) return <span className="text-t3 tabular-nums" title={t('superadminOps:billing.due.noPrice')}>{dfmt(due)}</span>;
    if (d < 0) return <StatusChip tone="danger">{t('superadminOps:billing.due.overdue', { count: Math.abs(d) })}</StatusChip>;
    if (d <= 15) return <StatusChip tone="warn">{d === 0 ? t('superadminOps:billing.due.today') : t('superadminOps:billing.due.soon', { count: d })}</StatusChip>;
    return <span className="text-t2 tabular-nums">{dfmt(due)}</span>;
  }, [t]);

  const columns = useMemo<Column<OverviewStore>[]>(() => [
    {
      key: 'store',
      header: t('superadminOps:billing.col.store'),
      width: 'minmax(180px, 2fr)',
      render: s => (
        <>
          <span className="block font-medium">{storeNameOf(s)}</span>
          <span className="block text-xs text-t3">
            {[s.organization_name, `#${s.id}`, !s.is_active ? t('superadminOps:billing.inactive') : ''].filter(Boolean).join(' · ')}
            {s.billing?.notes && (
              <>
                {' · '}
                <span title={s.billing.notes} className="underline decoration-dotted underline-offset-2">{t('superadminOps:billing.notes')}</span>
              </>
            )}
          </span>
        </>
      ),
      stackRender: s => storeNameOf(s),
    },
    {
      key: 'installed',
      header: t('superadminOps:billing.col.installed'),
      width: '108px',
      hideBelow: 'medium',
      render: s => <span className="text-t2 tabular-nums">{dfmt(s.billing?.installed_at)}</span>,
    },
    {
      key: 'nextDue',
      header: t('superadminOps:billing.col.nextDue'),
      width: '176px',
      render: s => dueCell(s.billing?.next_due_date, s.billing?.agreed_price != null),
    },
    {
      key: 'suggested',
      header: t('superadminOps:billing.col.suggested'),
      width: '116px',
      align: 'right',
      hideBelow: 'medium',
      render: s => (s.suggested_cycle_price > 0 ? <span className="text-t3">{money(s.suggested_cycle_price)}</span> : <span className="text-t4">—</span>),
    },
    {
      key: 'agreed',
      header: t('superadminOps:billing.col.agreed'),
      width: '124px',
      align: 'right',
      stack: 'end',
      render: s => (
        <>
          <span className="block font-semibold">{s.billing?.agreed_price != null ? money(s.billing.agreed_price) : '—'}</span>
          {s.billing && <span className="block text-xs text-t3">{cycleLabel(s.billing.billing_cycle)}</span>}
        </>
      ),
      stackRender: s => (s.billing?.agreed_price != null ? money(s.billing.agreed_price) : '—'),
    },
    {
      key: 'docs',
      header: t('superadminOps:billing.col.docs'),
      width: 'minmax(136px, 1fr)',
      render: s => <DocsUsage store={s} />,
      stackRender: s => (s.billing?.provider_doc_quota
        ? `${t('superadminOps:billing.col.docs')} ${fmtNum(s.docs_used)} / ${fmtNum(s.billing.provider_doc_quota)}`
        : t('superadminOps:billing.docsNoQuota', { used: fmtNum(s.docs_used) })),
    },
    {
      key: 'hardware',
      header: t('superadminOps:billing.col.hardware'),
      width: '160px',
      hideBelow: 'medium',
      render: s => {
        if (!(s.hw_items > 0)) return <span className="text-t4">—</span>;
        const hwBalance = s.hw_total - s.hw_paid;
        return hwBalance > 0
          ? <StatusChip tone="warn">{t('superadminOps:billing.hwOwes', { amount: money(hwBalance) })}</StatusChip>
          : <StatusChip tone="ok">{t('superadminOps:billing.hwSettled')}</StatusChip>;
      },
    },
  ], [t, cycleLabel, dueCell]);

  const filtered = stores.filter(s => matchesSearch(search, s.display_name, s.name, s.organization_name, `#${s.id}`));
  const sel = selId !== null ? stores.find(s => s.id === selId) ?? null : null;
  const firstLoad = loading && !loaded;

  const page = (
    <ListPage
      eyebrow={support ? t('superadminOps:eyebrowSupport') : t('superAdminPanel:nav.groups.finance')}
      title={t('superadminOps:billing.title')}
      subtitle={t('superadminOps:billing.subtitle')}
      actions={(
        <>
          <button type="button" className={GHOST_BTN} onClick={() => setShowBulk(true)} disabled={!loaded} data-testid="sa-billing-bulk-open">
            <ListChecks className="w-4 h-4" aria-hidden="true" />
            {t('superadminOps:billing.bulkButton')}
          </button>
          {!support && (
            <button type="button" className={GHOST_BTN} onClick={() => setShowFeaturesModal(true)} disabled={!loaded}>
              <Settings2 className="w-4 h-4" aria-hidden="true" />
              {t('superadminOps:billing.catalogButton')}
            </button>
          )}
        </>
      )}
      testId="sa-billing"
    >
      {loaded && state === 'ready' && (
        <div className="pb-5 -mt-1">
          <Stats testId="sa-billing-stats">
            <Stat
              label={t('superadminOps:billing.stats.overdue')}
              value={<span className={summary.overdue > 0 ? DANGER_INK : undefined}>{summary.overdue}</span>}
            />
            <Stat
              label={t('superadminOps:billing.stats.dueSoon')}
              value={<span className={summary.dueSoon > 0 ? WARN_INK : undefined}>{summary.dueSoon}</span>}
            />
            <Stat label={t('superadminOps:billing.stats.monthly')} value={money(summary.monthly)} hint={t('superadminOps:billing.stats.monthlyHint')} />
            <Stat label={t('superadminOps:billing.stats.hardware')} value={money(summary.hwOutstanding)} />
          </Stats>
        </div>
      )}

      <ListToolbar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t('superadminOps:billing.searchPlaceholder'),
          ariaLabel: t('superadminOps:billing.searchLabel'),
        }}
        actions={(
          <button type="button" className={GHOST_BTN} onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('superadminOps:refresh')}
          </button>
        )}
        summary={state === 'ready' && !firstLoad
          ? (search.trim()
            ? t('superadminOps:billing.countFiltered', { shown: filtered.length, total: stores.length })
            : t('superadminOps:billing.count', { count: stores.length }))
          : null}
      />

      {state !== 'ready' ? (
        <LoadFailed
          message={state === 'forbidden' ? t('superadminOps:forbidden') : t('superadminOps:billing.loadFailed')}
          onRetry={() => load()}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={s => s.id}
          onRowClick={s => setSelId(s.id)}
          selectedKey={selId}
          loading={loading}
          stickyHeader
          rowClassName={s => (!s.is_active ? 'opacity-60' : undefined)}
          rowLabel={s => t('superadminOps:billing.rowLabel', { store: storeNameOf(s) })}
          empty={search.trim()
            ? <EmptyState title={t('superadminOps:billing.noMatch')} hint={t('superadminOps:billing.noMatchHint')} />
            : <EmptyState title={t('superadminOps:billing.empty')} />}
          testId="sa-billing-table"
        />
      )}

      {showBulk && (
        <BulkBillingPanel stores={stores} cycleLabel={cycleLabel} onClose={() => setShowBulk(false)} onSaved={load} />
      )}

      {showFeaturesModal && (
        <FeatureCatalogPanel
          catalog={catalog}
          onClose={() => setShowFeaturesModal(false)}
          onChanged={load}
        />
      )}

      {sel && (
        <StoreBillingPanel
          key={sel.id}
          store={sel}
          catalog={catalog}
          detectableCodes={detectableCodes}
          cycleLabel={cycleLabel}
          onClose={() => setSelId(null)}
          onChanged={load}
        />
      )}
    </ListPage>
  );

  // Στο SupportPanel η καρτέλα αποδίδεται χωρίς περιθώρια σελίδας (το πλαίσιο του super admin τα έχει).
  return support ? <div className="px-4 py-4 md:px-6 md:py-5">{page}</div> : page;
};

/** Ερώτηση πάνω από το συρτάρι: ConfirmModal με τα κείμενα της ενέργειας. */
interface ConfirmSpec {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  variant: 'danger' | 'warning';
  run: () => void;
}

// ── Μαζική συμπλήρωση κύκλου/τιμής (27/10/2026) ───────────────────────────────
// Μετά τη συμπλήρωση από τα δεδομένα (εγκατάσταση = πρώτη απόδειξη) λείπει σε σχεδόν όλα η τιμή: ένας πίνακας αντί
// για 37 συρτάρια. Μόνο κύκλος και συμφωνημένη τιμή· στέλνονται μόνο οι γραμμές που άλλαξαν, όλες μαζί ή καμία.
const HQ_STORE_ID = 1;
const PRICE_RE = /^\d+(\.\d{1,2})?$/;
interface BulkRow { cycle: string; price: string }
const bulkRowOf = (s: OverviewStore): BulkRow => ({
  cycle: s.billing?.billing_cycle || 'monthly',
  price: s.billing?.agreed_price != null ? String(parseFloat(s.billing.agreed_price)) : '',
});

const BulkBillingPanel: React.FC<{
  stores: OverviewStore[]; cycleLabel: (v?: string) => string; onClose: () => void; onSaved: () => void;
}> = ({ stores, cycleLabel, onClose, onSaved }) => {
  const { t } = useTranslation(['superadminOps', 'common', 'listPage']);
  const { showToast } = useToast();
  const active = useMemo(() => stores.filter(s => s.is_active && s.id !== HQ_STORE_ID), [stores]);
  // Οι τιμές του server τη στιγμή που άνοιξε: μια επαναφόρτωση της λίστας από πίσω δεν σβήνει ό,τι γράφτηκε.
  const [initial] = useState<Record<number, BulkRow>>(() => Object.fromEntries(active.map(s => [s.id, bulkRowOf(s)])));
  const [rows, setRows] = useState<Record<number, BulkRow>>(initial);
  const [onlyMissing, setOnlyMissing] = useState(() => active.some(s => s.billing?.agreed_price == null));
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [discard, setDiscard] = useState(false);

  const norm = (p: string) => p.trim().replace(',', '.');
  const changed = active.filter(s => initial[s.id] && rows[s.id]
    && (initial[s.id].cycle !== rows[s.id].cycle || norm(initial[s.id].price) !== norm(rows[s.id].price)));
  const invalid = new Set(changed.filter(s => norm(rows[s.id].price) !== '' && !PRICE_RE.test(norm(rows[s.id].price))).map(s => s.id));
  // Το φίλτρο κοιτά τα στοιχεία του server — μια γραμμή δεν χάνεται όσο γράφεις την τιμή της.
  const shown = active
    .filter(s => !onlyMissing || initial[s.id]?.price === '')
    .filter(s => matchesSearch(search, s.display_name, s.name, s.organization_name, `#${s.id}`));

  const setRow = (id: number, patch: Partial<BulkRow>) => setRows(r => ({ ...r, [id]: { ...r[id], ...patch } }));

  const requestClose = () => {
    if (discard) { setDiscard(false); return; }
    if (busy) return;
    if (changed.length) { setDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    if (!changed.length || invalid.size) return;
    try {
      setBusy(true);
      const r: any = await adminAPI.saveBillingBulk(changed.map(s => ({
        store_id: s.id, billing_cycle: rows[s.id].cycle, agreed_price: norm(rows[s.id].price) === '' ? null : norm(rows[s.id].price),
      })));
      showToast('success', t('superadminOps:billing.bulk.saved', { count: r?.updated ?? changed.length }));
      onSaved();
      onClose();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || t('superadminOps:billing.bulk.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailPanel
      open
      width="lg"
      title={t('superadminOps:billing.bulk.title')}
      subtitle={t('superadminOps:billing.bulk.subtitle')}
      onClose={requestClose}
      footerStart={invalid.size ? <p role="alert" className="text-sm text-red-700 dark:text-red-400">{t('superadminOps:billing.bulk.invalid')}</p> : undefined}
      footer={(
        <>
          <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={busy}>{t('listPage:close')}</button>
          <button type="button" className={PRIMARY_BTN} onClick={save} disabled={busy || !changed.length || invalid.size > 0} data-testid="sa-billing-bulk-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {changed.length ? t('superadminOps:billing.bulk.save', { count: changed.length }) : t('superadminOps:billing.bulk.noChanges')}
          </button>
        </>
      )}
      testId="sa-billing-bulk"
    >
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t('superadminOps:billing.searchPlaceholder')} aria-label={t('superadminOps:billing.searchLabel')}
          className={`${INPUT_CLS} flex-1 min-w-[180px]`}
        />
        <label className="inline-flex items-center gap-2 text-sm text-t2 cursor-pointer">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} className="w-4 h-4 accent-brand" />
          {t('superadminOps:billing.bulk.onlyMissing')}
        </label>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-t3 py-6 text-center">{t('superadminOps:billing.bulk.empty')}</p>
      ) : (
        <ul className="divide-y divide-line" aria-label={t('superadminOps:billing.bulk.title')}>
          {shown.map(s => {
            const row = rows[s.id];
            const suggested = s.suggested_monthly * cycleMonths(row.cycle);
            const bad = invalid.has(s.id);
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5" data-testid={`sa-billing-bulk-${s.id}`}>
                <div className="min-w-0 flex-1 basis-48">
                  <p className="text-sm font-medium text-t1 truncate">{storeNameOf(s)}</p>
                  <p className="text-xs text-t3 truncate">
                    {[s.organization_name, `#${s.id}`, s.billing?.installed_at ? t('superadminOps:billing.bulk.installed', { date: dfmt(s.billing.installed_at) }) : null]
                      .filter(Boolean).join(' · ')}
                  </p>
                </div>
                <select
                  value={row.cycle} onChange={(e) => setRow(s.id, { cycle: e.target.value })}
                  aria-label={t('superadminOps:billing.bulk.cycleLabel', { store: storeNameOf(s) })}
                  className={`${SELECT_CLS} w-36 shrink-0`}
                >
                  {CYCLES.map(c => <option key={c.value} value={c.value}>{cycleLabel(c.value)}</option>)}
                </select>
                <div className="relative w-32 shrink-0">
                  <input
                    value={row.price} inputMode="decimal"
                    onChange={(e) => setRow(s.id, { price: e.target.value })}
                    placeholder={suggested > 0 ? suggested.toFixed(2) : '0.00'}
                    aria-label={t('superadminOps:billing.bulk.priceLabel', { store: storeNameOf(s) })}
                    aria-invalid={bad || undefined}
                    title={suggested > 0 ? t('superadminOps:billing.bulk.suggested', { amount: money(suggested) }) : undefined}
                    className={`${INPUT_CLS} pr-8 text-right tabular-nums ${bad ? 'border-red-500' : ''}`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-t3" aria-hidden="true">€</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmModal
        isOpen={discard}
        variant="warning"
        title={t('superadminOps:discard.title')}
        message={t('superadminOps:discard.message')}
        confirmLabel={t('superadminOps:discard.confirm')}
        cancelLabel={t('superadminOps:discard.keep')}
        onConfirm={() => { setDiscard(false); onClose(); }}
        onCancel={() => setDiscard(false)}
      />
    </DetailPanel>
  );
};

// ── Κατάλογος λειτουργιών & τιμών ─────────────────────────────────────────────
const FeatureCatalogPanel: React.FC<{ catalog: FeatureCatalogItem[]; onClose: () => void; onChanged: () => void }> = ({ catalog, onClose, onChanged }) => {
  const { t } = useTranslation(['superadminOps', 'common', 'listPage']);
  const { showToast } = useToast();
  const [rows, setRows] = useState(() => catalog.map(c => ({ ...c, monthly_price: String(c.monthly_price) })));
  const [newFeature, setNewFeature] = useState({ name: '', monthly_price: '' });
  const [busy, setBusy] = useState(false);
  const [discard, setDiscard] = useState(false);

  // Αλλαγές που δεν αποθηκεύτηκαν (σε σχέση με τον κατάλογο του server): ρωτάμε πριν κλείσει.
  const dirty = newFeature.name.trim() !== '' || newFeature.monthly_price !== '' || rows.some(r => {
    const c = catalog.find(x => x.id === r.id);
    return !c || c.name !== r.name || (parseFloat(String(c.monthly_price)) || 0) !== (parseFloat(r.monthly_price) || 0);
  });

  const requestClose = () => {
    if (discard) { setDiscard(false); return; }
    if (busy) return;
    if (dirty) { setDiscard(true); return; }
    onClose();
  };

  const saveRow = async (row: any) => {
    try {
      setBusy(true);
      await adminAPI.updateBillingFeature(row.id, { name: row.name, monthly_price: parseFloat(row.monthly_price) || 0, is_active: row.is_active });
      onChanged();
    } catch (e: any) { showToast('error', e.response?.data?.error || t('superadminOps:billing.failed')); } finally { setBusy(false); }
  };

  const addFeature = async () => {
    if (!newFeature.name.trim()) return;
    try {
      setBusy(true);
      await adminAPI.createBillingFeature({ name: newFeature.name.trim(), monthly_price: parseFloat(newFeature.monthly_price) || 0 });
      setNewFeature({ name: '', monthly_price: '' });
      onChanged();
      onClose();
    } catch (e: any) { showToast('error', e.response?.data?.error || t('superadminOps:billing.failed')); } finally { setBusy(false); }
  };

  return (
    <DetailPanel
      open
      title={t('superadminOps:billing.catalog.title')}
      subtitle={t('superadminOps:billing.catalog.subtitle')}
      onClose={requestClose}
      footer={<button type="button" className={GHOST_BTN} onClick={requestClose}>{t('listPage:close')}</button>}
      testId="sa-billing-catalog"
    >
      {rows.length === 0 ? (
        <p className="text-sm text-t3">{t('superadminOps:billing.catalog.empty')}</p>
      ) : (
        <ul className="divide-y divide-line" aria-label={t('superadminOps:billing.catalog.title')}>
          {rows.map((row, i) => (
            <li key={row.id} className="flex items-center gap-2 py-2.5" data-testid={`sa-billing-feature-${row.id}`}>
              <div className={`flex-1 min-w-0 ${!row.is_active ? 'opacity-60' : ''}`}>
                <input
                  value={row.name}
                  aria-label={t('superadminOps:billing.catalog.name')}
                  onChange={(e) => setRows(r => r.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  className={INPUT_CLS}
                />
              </div>
              <div className={`relative w-28 shrink-0 ${!row.is_active ? 'opacity-60' : ''}`}>
                <input
                  value={row.monthly_price}
                  inputMode="decimal"
                  aria-label={t('superadminOps:billing.catalog.price', { name: row.name })}
                  onChange={(e) => setRows(r => r.map((x, j) => j === i ? { ...x, monthly_price: e.target.value.replace(',', '.') } : x))}
                  className={`${INPUT_CLS} pr-8 text-right tabular-nums`}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-t3" aria-hidden="true">€</span>
              </div>
              {!row.is_active && <StatusChip tone="muted">{t('superadminOps:billing.catalog.inactive')}</StatusChip>}
              <button
                type="button"
                onClick={() => saveRow(row)}
                disabled={busy}
                className={ICON_BTN}
                title={t('superadminOps:save')}
                aria-label={t('superadminOps:billing.catalog.saveRow', { name: row.name })}
              >
                <Check className="w-4 h-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => { const upd = { ...row, is_active: !row.is_active }; setRows(r => r.map((x, j) => j === i ? upd : x)); saveRow(upd); }}
                disabled={busy}
                className={ICON_BTN}
                title={row.is_active ? t('superadminOps:billing.catalog.deactivateShort') : t('superadminOps:billing.catalog.activateShort')}
                aria-label={row.is_active
                  ? t('superadminOps:billing.catalog.deactivate', { name: row.name })
                  : t('superadminOps:billing.catalog.activate', { name: row.name })}
              >
                {row.is_active ? <Trash2 className="w-4 h-4" aria-hidden="true" /> : <Plus className="w-4 h-4" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 pt-5 border-t border-line">
        <h3 className="text-sm font-semibold text-t1 mb-3">{t('superadminOps:billing.catalog.new')}</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[180px]">
            <input
              value={newFeature.name}
              aria-label={t('superadminOps:billing.catalog.newName')}
              onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
              placeholder={t('superadminOps:billing.catalog.newPlaceholder')}
              className={INPUT_CLS}
            />
          </div>
          <div className="relative w-28 shrink-0">
            <input
              value={newFeature.monthly_price}
              inputMode="decimal"
              aria-label={t('superadminOps:billing.catalog.newPrice')}
              onChange={(e) => setNewFeature({ ...newFeature, monthly_price: e.target.value.replace(',', '.') })}
              placeholder="0.00"
              className={`${INPUT_CLS} pr-8 text-right tabular-nums`}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-t3" aria-hidden="true">€</span>
          </div>
          <button type="button" onClick={addFeature} disabled={busy || !newFeature.name.trim()} className={GHOST_BTN}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('superadminOps:billing.catalog.add')}
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={discard}
        variant="warning"
        title={t('superadminOps:discard.title')}
        message={t('superadminOps:discard.message')}
        confirmLabel={t('superadminOps:discard.confirm')}
        cancelLabel={t('superadminOps:discard.keep')}
        onConfirm={() => { setDiscard(false); onClose(); }}
        onCancel={() => setDiscard(false)}
      />
    </DetailPanel>
  );
};

// ── Καρτέλα τιμολόγησης καταστήματος ─────────────────────────────────────────
interface BillingForm {
  installed_at: string; billing_cycle: string; contract_end: string; tracking_from: string;
  agreed_price: string; provider_doc_quota: string; notes: string;
}
const EMPTY_FORM: BillingForm = {
  installed_at: '', billing_cycle: 'monthly', contract_end: '', tracking_from: '',
  agreed_price: '', provider_doc_quota: '', notes: '',
};

const StoreBillingPanel: React.FC<{
  store: OverviewStore; catalog: FeatureCatalogItem[]; detectableCodes: string[];
  cycleLabel: (v?: string) => string;
  onClose: () => void; onChanged: () => void;
}> = ({ store, catalog, detectableCodes, cycleLabel, onClose, onChanged }) => {
  const { t } = useTranslation(['superadminOps', 'common', 'listPage']);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<BillingForm>(EMPTY_FORM);
  const [checked, setChecked] = useState<Record<number, { on: boolean; override: string }>>({});
  const [payments, setPayments] = useState<any[]>([]);
  const [hardware, setHardware] = useState<any[]>([]);
  const [nextDue, setNextDue] = useState<string>('');
  const [newPayment, setNewPayment] = useState({ amount: '', paid_at: athensYmd(), period_covered: '' });
  const [newHw, setNewHw] = useState({ item: '', sold_at: athensYmd(), price: '', paid_amount: '' });
  // Τι ήρθε από τον server (για το «Κλείσιμο χωρίς αποθήκευση;») και η αποθηκευμένη συμφωνημένη χρέωση.
  const [baseForm, setBaseForm] = useState<string | null>(null);
  const [baseChecked, setBaseChecked] = useState<string | null>(null);
  const [savedAgreed, setSavedAgreed] = useState('');
  const [paymentError, setPaymentError] = useState(false);
  const [hwError, setHwError] = useState(false);
  // Περίοδος χωρίς συμφωνημένη χρέωση: πρώτα το ποσό (στη θέση του παλιού prompt), μετά η ερώτηση.
  const [amountFor, setAmountFor] = useState<{ period: BillingPeriod; value: string; error: boolean } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  // Ο κατάλογος διαβάζεται τη στιγμή της φόρτωσης: μια επαναφόρτωση της λίστας (νέος πίνακας) δεν
  // ξαναχτίζει την καρτέλα — θα έσβηνε τις αλλαγές που δεν αποθηκεύτηκαν.
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const d: any = await adminAPI.getStoreBilling(store.id);
      const b = d.billing || {};
      const nextForm: BillingForm = {
        installed_at: dstr(b.installed_at),
        billing_cycle: b.billing_cycle || 'monthly',
        contract_end: dstr(b.contract_end),
        tracking_from: dstr(b.tracking_from),
        agreed_price: b.agreed_price != null ? String(b.agreed_price) : '',
        provider_doc_quota: b.provider_doc_quota != null ? String(b.provider_doc_quota) : '',
        notes: b.notes || '',
      };
      setForm(nextForm);
      const map: Record<number, { on: boolean; override: string }> = {};
      for (const f of catalogRef.current) map[f.id] = { on: false, override: '' };
      for (const f of (d.features || [])) {
        map[f.feature_id] = { on: true, override: f.price_override != null ? String(f.price_override) : '' };
      }
      setChecked(map);
      setPayments(d.payments || []);
      setHardware(d.hardware || []);
      setNextDue(dstr(b.next_due_date));
      const agreed = b.agreed_price != null ? String(b.agreed_price) : '';
      setNewPayment(p => ({ ...p, amount: agreed }));
      setSavedAgreed(agreed);
      setBaseForm(JSON.stringify(nextForm));
      setBaseChecked(JSON.stringify(map));
    } catch (e) {
      console.error(e);
      // Χωρίς τα στοιχεία του server η φόρμα θα ήταν κενή — και η «Αποθήκευση» θα τα έσβηνε. Μόνο «Δοκιμάστε ξανά».
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [store.id]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Ελαφρύ refresh μετά από πληρωμή/hardware — ΔΕΝ πειράζει form/checked ώστε
  // να μη χαθούν αναποθήκευτες αλλαγές του χρήστη στη φόρμα.
  const refreshLists = async () => {
    const d: any = await adminAPI.getStoreBilling(store.id);
    setPayments(d.payments || []);
    setHardware(d.hardware || []);
    setNextDue(dstr(d.billing?.next_due_date));
  };

  // Μόνο οι ΕΝΕΡΓΕΣ λειτουργίες εμφανίζονται για επιλογή και μετράνε στην
  // προτεινόμενη τιμή. Assignments σε πλέον-ανενεργές λειτουργίες παραμένουν
  // στο `checked` (δεν χάνονται στο save) αλλά δεν κοστολογούνται.
  const activeCatalog = useMemo(() => catalog.filter(f => f.is_active), [catalog]);
  const detected = useMemo(() => new Set(store.detected_codes || []), [store.detected_codes]);
  const detectable = useMemo(() => new Set(detectableCodes), [detectableCodes]);

  // Τικάρει τις ανιχνεύσιμες λειτουργίες σύμφωνα με τα πραγματικά flags του
  // καταστήματος. Μη ανιχνεύσιμες (π.χ. Kitchen Display) μένουν όπως είναι.
  const autoDetect = () => {
    setChecked(m => {
      const next = { ...m };
      for (const f of activeCatalog) {
        if (!detectable.has(f.code)) continue;
        const isOn = detected.has(f.code);
        next[f.id] = { on: isOn, override: isOn ? (m[f.id]?.override || '') : '' };
      }
      return next;
    });
  };

  const suggestedMonthly = useMemo(() => {
    let sum = 0;
    for (const f of activeCatalog) {
      const c = checked[f.id];
      if (!c?.on) continue;
      const ov = parseFloat(c.override);
      sum += Number.isFinite(ov) ? ov : (parseFloat(String(f.monthly_price)) || 0);
    }
    return sum;
  }, [activeCatalog, checked]);
  const suggestedCycle = suggestedMonthly * cycleMonths(form.billing_cycle);

  const persistBilling = () => adminAPI.saveStoreBilling(store.id, {
    installed_at: form.installed_at || null,
    billing_cycle: form.billing_cycle,
    contract_end: form.contract_end || null,
    tracking_from: form.tracking_from || null,
    agreed_price: form.agreed_price !== '' ? parseFloat(form.agreed_price) : null,
    provider_doc_quota: form.provider_doc_quota !== '' ? parseInt(form.provider_doc_quota, 10) : null,
    notes: form.notes || null,
  });

  // Περίοδοι χρέωσης από τα ΤΡΕΧΟΝΤΑ στοιχεία της φόρμας (live preview —
  // αλλαγή εγκατάστασης/κύκλου ενημερώνει αμέσως το ημερολόγιο).
  const periods = useMemo(() => {
    if (!form.installed_at) return [];
    return buildPeriods(form.installed_at, form.billing_cycle, form.contract_end || null, payments, form.tracking_from || null);
  }, [form.installed_at, form.billing_cycle, form.contract_end, form.tracking_from, payments]);

  const failure = (e: any, fallback: string) => showToast('error', e?.response?.data?.error || fallback);

  const doPay = async (p: BillingPeriod, amt: number) => {
    try {
      setSaving(true);
      // Πρώτα αποθηκεύονται τα στοιχεία της φόρμας ώστε το period_start να
      // δένει με τα ίδια installed_at/κύκλο που βλέπει και το backend.
      await persistBilling();
      setBaseForm(JSON.stringify(form));
      setSavedAgreed(form.agreed_price);
      await adminAPI.addBillingPayment(store.id, {
        amount: amt, paid_at: todayISO(), period_covered: p.label, period_start: p.start,
      });
      await refreshLists();
      onChanged();
    } catch (e: any) { failure(e, t('superadminOps:billing.store.payFailed')); } finally { setSaving(false); }
  };

  const askPay = (p: BillingPeriod, amt: number) => setConfirm({
    title: t('superadminOps:billing.store.payTitle'),
    message: t('superadminOps:billing.store.payConfirm', { amount: money(amt), label: p.label }),
    confirmLabel: t('superadminOps:billing.store.payButton'),
    variant: 'warning',
    run: () => doPay(p, amt),
  });

  const payPeriod = (p: BillingPeriod) => {
    const amt = parseFloat(form.agreed_price);
    if (!Number.isFinite(amt)) {
      setAmountFor({ period: p, value: '', error: false });
      return;
    }
    askPay(p, amt);
  };

  const submitAmount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amountFor) return;
    const amt = parseFloat(amountFor.value.replace(',', '.'));
    if (!Number.isFinite(amt)) { setAmountFor({ ...amountFor, error: true }); return; }
    const period = amountFor.period;
    setAmountFor(null);
    askPay(period, amt);
  };

  const unpayPeriod = (p: BillingPeriod) => {
    if (p.paymentId == null) return;
    const paymentId = p.paymentId;
    setConfirm({
      title: t('superadminOps:billing.store.unpayTitle'),
      message: t('superadminOps:billing.store.unpayConfirm', { label: p.label }),
      confirmLabel: t('superadminOps:billing.store.unpayButton'),
      variant: 'danger',
      run: async () => {
        try {
          setSaving(true);
          await adminAPI.deleteBillingPayment(paymentId);
          await refreshLists();
          onChanged();
        } catch (e: any) { failure(e, t('superadminOps:billing.failed')); } finally { setSaving(false); }
      },
    });
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      await persistBilling();
      // Χτίζεται από ΟΛΑ τα checked entries (όχι μόνο τον ενεργό κατάλογο) ώστε
      // το replace-all PUT να ΜΗΝ σβήνει σιωπηλά assignments σε λειτουργίες που
      // απενεργοποιήθηκαν στον κατάλογο μετά την ανάθεσή τους.
      await adminAPI.saveStoreBillingFeatures(store.id,
        Object.entries(checked)
          .filter(([, c]) => c.on)
          .map(([featureId, c]) => ({
            feature_id: parseInt(featureId, 10),
            price_override: c.override !== '' ? parseFloat(c.override) : null,
          }))
      );
      onChanged();
      onClose();
    } catch (e: any) {
      failure(e, t('superadminOps:billing.store.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const addPayment = async () => {
    const amt = parseFloat(newPayment.amount);
    if (!Number.isFinite(amt)) { setPaymentError(true); return; }
    try {
      setSaving(true);
      await adminAPI.addBillingPayment(store.id, {
        amount: amt, paid_at: newPayment.paid_at || undefined,
        period_covered: newPayment.period_covered || undefined,
      });
      // Όπως όταν η καρτέλα ξαναχτιζόταν: καθαρή γραμμή, ποσό = η αποθηκευμένη συμφωνημένη χρέωση
      // (ώστε ένα δεύτερο πάτημα να μη γράψει την ίδια πληρωμή δύο φορές).
      setNewPayment({ amount: savedAgreed, paid_at: athensYmd(), period_covered: '' });
      await refreshLists();
      onChanged();
    } catch (e: any) { failure(e, t('superadminOps:billing.failed')); } finally { setSaving(false); }
  };

  const askDeletePayment = (p: any) => setConfirm({
    title: t('superadminOps:billing.store.deletePaymentTitle'),
    message: t('superadminOps:billing.store.deletePaymentConfirm'),
    confirmLabel: t('superadminOps:delete'),
    variant: 'danger',
    run: async () => {
      try {
        await adminAPI.deleteBillingPayment(p.id);
        await refreshLists();
        onChanged();
      } catch (e: any) { failure(e, t('superadminOps:billing.failed')); }
    },
  });

  const addHardware = async () => {
    if (!newHw.item.trim()) { setHwError(true); return; }
    try {
      setSaving(true);
      await adminAPI.addStoreHardware(store.id, {
        item: newHw.item.trim(), sold_at: newHw.sold_at || undefined,
        price: parseFloat(newHw.price) || 0, paid_amount: parseFloat(newHw.paid_amount) || 0,
      });
      setNewHw({ item: '', sold_at: athensYmd(), price: '', paid_amount: '' });
      await refreshLists();
      onChanged();
    } catch (e: any) { failure(e, t('superadminOps:billing.failed')); } finally { setSaving(false); }
  };

  const updateHwPaid = async (hw: any, paid: string) => {
    try {
      await adminAPI.updateStoreHardware(hw.id, { paid_amount: parseFloat(paid) || 0, sold_at: dstr(hw.sold_at) || null, notes: hw.notes });
      await refreshLists();
      onChanged();
    } catch { /* toast-less: η λίστα ξαναφορτώνει */ }
  };

  const askDeleteHardware = (h: any) => setConfirm({
    title: t('superadminOps:billing.store.hwDeleteTitle'),
    message: t('superadminOps:billing.store.hwDeleteConfirm'),
    confirmLabel: t('superadminOps:delete'),
    variant: 'danger',
    run: async () => {
      try {
        await adminAPI.deleteStoreHardware(h.id);
        await loadDetail();
        onChanged();
      } catch (e: any) { failure(e, t('superadminOps:billing.failed')); }
    },
  });

  const dirty = baseForm !== null && baseChecked !== null
    && (JSON.stringify(form) !== baseForm || JSON.stringify(checked) !== baseChecked);

  // Esc / φόντο / X / «Κλείσιμο»: πρώτα κλείνει ό,τι είναι ανοιχτό από πάνω (ερώτηση, ποσό περιόδου)·
  // με αλλαγές που δεν αποθηκεύτηκαν, ρωτά.
  const requestClose = () => {
    if (confirm) { setConfirm(null); return; }
    if (amountFor) { setAmountFor(null); return; }
    if (saving) return;
    if (dirty) {
      setConfirm({
        title: t('superadminOps:discard.title'),
        message: t('superadminOps:discard.message'),
        confirmLabel: t('superadminOps:discard.confirm'),
        cancelLabel: t('superadminOps:discard.keep'),
        variant: 'warning',
        run: onClose,
      });
      return;
    }
    onClose();
  };

  const setField = <K extends keyof BillingForm>(key: K, value: BillingForm[K]) => setForm(f => ({ ...f, [key]: value }));

  const periodStatusText = (s: BillingPeriod['status']) => t(`superadminOps:billing.store.period.${s}`);

  const commercial = (
    <div className="xs-grid2">
      <SheetField label={t('superadminOps:billing.store.installedAt')} htmlFor="sa-billing-installed">
        <input id="sa-billing-installed" type="date" value={form.installed_at} onChange={(e) => setField('installed_at', e.target.value)} className={DATE_CLS} />
      </SheetField>
      <SheetField label={t('superadminOps:billing.store.cycle')} htmlFor="sa-billing-cycle">
        <select id="sa-billing-cycle" value={form.billing_cycle} onChange={(e) => setField('billing_cycle', e.target.value)} className={SELECT_CLS}>
          {CYCLES.map(c => <option key={c.value} value={c.value}>{cycleLabel(c.value)}</option>)}
        </select>
      </SheetField>
      <SheetField label={t('superadminOps:billing.store.contractEnd')} htmlFor="sa-billing-contract-end">
        <input id="sa-billing-contract-end" type="date" value={form.contract_end} onChange={(e) => setField('contract_end', e.target.value)} className={DATE_CLS} />
      </SheetField>
      <SheetField label={t('superadminOps:billing.store.agreedPrice')} htmlFor="sa-billing-agreed">
        <input
          id="sa-billing-agreed"
          inputMode="decimal"
          value={form.agreed_price}
          onChange={(e) => setField('agreed_price', e.target.value.replace(',', '.'))}
          placeholder={suggestedCycle > 0 ? t('superadminOps:billing.store.agreedPlaceholder', { amount: suggestedCycle.toFixed(2) }) : '0.00'}
          className={`${INPUT_CLS} tabular-nums`}
        />
      </SheetField>
      <SheetField label={t('superadminOps:billing.store.docQuota')} htmlFor="sa-billing-quota">
        <input
          id="sa-billing-quota"
          inputMode="numeric"
          value={form.provider_doc_quota}
          onChange={(e) => setField('provider_doc_quota', e.target.value.replace(/\D/g, ''))}
          placeholder={t('superadminOps:billing.store.docQuotaPlaceholder')}
          className={`${INPUT_CLS} tabular-nums`}
        />
      </SheetField>
      <SheetField label={t('superadminOps:billing.store.trackingFrom')} htmlFor="sa-billing-tracking" hint={t('superadminOps:billing.store.trackingFromHint')}>
        <input id="sa-billing-tracking" type="date" value={form.tracking_from} onChange={(e) => setField('tracking_from', e.target.value)} className={DATE_CLS} />
      </SheetField>
      <SheetLocked label={t('superadminOps:billing.store.nextDue')} value={nextDue ? dfmt(nextDue) : null} hint={t('superadminOps:billing.store.nextDueHint')} />
    </div>
  );

  const body = loading ? (
    <div role="status" className="flex items-center justify-center gap-2 py-24 text-t3">
      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
      <span className="text-sm">{t('listPage:loading')}</span>
    </div>
  ) : loadFailed ? (
    <LoadFailed message={t('superadminOps:billing.store.loadFailed')} onRetry={() => loadDetail()} />
  ) : (
    // Οι ενότητες με τη μικρή μπάρα του «Φύλλου»· η πρώτη χωρίς γραμμή από πάνω (την έχει η κεφαλίδα).
    <div className="[&>section:first-child]:border-t-0 [&>section:first-child]:pt-0">
      {/* Εμπορικά στοιχεία */}
      <SheetSection id="sa-billing-commercial" title={t('superadminOps:billing.store.commercial')} hint={t('superadminOps:billing.store.commercialHint')}>
        {commercial}
        <div className="mt-5">
          <Stats testId="sa-billing-docs">
            <Stat
              label={t('superadminOps:billing.store.docsUsed')}
              value={t('superadminOps:billing.store.docsUsedValue', { count: store.docs_used, value: fmtNum(store.docs_used) })}
              hint={t('superadminOps:billing.store.docsRaw', { count: store.docs_raw, value: fmtNum(store.docs_raw) })}
            />
            {form.provider_doc_quota && (
              <Stat
                label={t('superadminOps:billing.store.docsLeft')}
                value={fmtNum(Math.max(0, parseInt(form.provider_doc_quota, 10) - store.docs_used))}
              />
            )}
          </Stats>
        </div>
      </SheetSection>

      {/* Λειτουργίες */}
      <SheetSection
        id="sa-billing-features"
        title={t('superadminOps:billing.store.features')}
        hint={(
          <>
            {t('superadminOps:billing.store.suggested')}{' '}
            <b className="font-semibold text-t1">{t('superadminOps:billing.store.perMonth', { amount: money(suggestedMonthly) })}</b>
            {cycleMonths(form.billing_cycle) > 1 && (
              <> {t('superadminOps:billing.store.suggestedCycle', { amount: money(suggestedCycle), cycle: cycleLabel(form.billing_cycle).toLowerCase() })}</>
            )}
          </>
        )}
        aside={(
          <button type="button" onClick={autoDetect} className={ASIDE_BTN} title={t('superadminOps:billing.store.autoDetectHint')}>
            <Zap className="w-4 h-4" aria-hidden="true" />
            {t('superadminOps:billing.store.autoDetect')}
          </button>
        )}
        testId="sa-billing-features"
      >
        <ul className="divide-y divide-line">
          {activeCatalog.map(f => {
            const c = checked[f.id] || { on: false, override: '' };
            const canDetect = detectable.has(f.code);
            const isDetected = detected.has(f.code);
            // Αναντιστοιχίες χρέωσης ↔ πραγματικότητας
            const unchargedActive = canDetect && isDetected && !c.on;   // το έχει, δεν χρεώνεται
            const chargedInactive = canDetect && !isDetected && c.on;   // χρεώνεται, δεν το έχει
            return (
              <li key={f.id} className="flex items-center gap-3 py-2 min-h-[52px]" data-testid={`sa-billing-store-feature-${f.id}`}>
                <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="checkbox" checked={c.on}
                    onChange={() => setChecked(m => ({ ...m, [f.id]: { ...c, on: !c.on } }))}
                    className="w-4 h-4 shrink-0 accent-brand"
                  />
                  <span className="text-sm text-t1 truncate">{f.name}</span>
                  {unchargedActive && (
                    <StatusChip tone="warn" title={t('superadminOps:billing.store.unchargedTitle')}>{t('superadminOps:billing.store.uncharged')}</StatusChip>
                  )}
                  {chargedInactive && (
                    <StatusChip tone="muted" title={t('superadminOps:billing.store.chargedInactiveTitle')}>{t('superadminOps:billing.store.chargedInactive')}</StatusChip>
                  )}
                </label>
                {c.on ? (
                  <div className="w-28 shrink-0">
                    <input
                      value={c.override}
                      inputMode="decimal"
                      onChange={(e) => setChecked(m => ({ ...m, [f.id]: { ...c, override: e.target.value.replace(',', '.') } }))}
                      placeholder={String(parseFloat(String(f.monthly_price)) || 0)}
                      className={`${INPUT_CLS} text-right tabular-nums`}
                      title={t('superadminOps:billing.store.overrideTitle')}
                      aria-label={t('superadminOps:billing.store.override', { name: f.name })}
                    />
                  </div>
                ) : (
                  <span className="shrink-0 text-sm text-t3 tabular-nums">{money(f.monthly_price)}</span>
                )}
              </li>
            );
          })}
        </ul>
      </SheetSection>

      {/* Σχόλια */}
      <SheetSection id="sa-billing-notes" title={t('superadminOps:billing.store.notes')}>
        <textarea
          value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={3}
          aria-label={t('superadminOps:billing.store.notes')}
          placeholder={t('superadminOps:billing.store.notesPlaceholder')}
          className={`${INPUT_CLS} resize-y`}
        />
      </SheetSection>

      {/* Πληρωμές πάγιου */}
      <SheetSection
        id="sa-billing-payments"
        title={t('superadminOps:billing.store.payments')}
        hint={form.installed_at
          ? (form.contract_end ? t('superadminOps:billing.store.periodsHintContract') : t('superadminOps:billing.store.periodsHint'))
          : t('superadminOps:billing.store.noInstall')}
        testId="sa-billing-payments"
      >
        {form.installed_at && (
          <>
            {/* Υπόμνημα με κουκκίδες (όχι ετικέτες: θα έμοιαζαν με τις περιόδους που πατιούνται). Το νόημα
                κάθε περιόδου το λέει και το όνομά της για τους αναγνώστες οθόνης. */}
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-xs text-t3" aria-hidden="true">
              {(['paid', 'overdue', 'current', 'future', 'untracked'] as const)
                .filter(s => s !== 'untracked' || periods.some(p => p.status === 'untracked')).map(s => (
                <span key={s} className="inline-flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${PERIOD_DOT[s]}`} />
                  {periodStatusText(s)}
                </span>
              ))}
            </p>
            {/* Ημερολόγιο περιόδων: κλικ σε απλήρωτη → καταχώρηση, κλικ σε πληρωμένη → αναίρεση */}
            <div className="flex flex-wrap gap-1 max-h-44 overflow-y-auto pr-1" data-testid="sa-billing-periods">
              {periods.map(p => (
                <button
                  key={p.start}
                  type="button"
                  disabled={saving}
                  onClick={() => (p.status === 'paid' ? unpayPeriod(p) : payPeriod(p))}
                  title={p.status === 'paid'
                    ? t('superadminOps:billing.store.periodTitlePaid', { from: dfmt(p.start), to: dfmt(p.end) })
                    : p.status === 'untracked'
                      ? t('superadminOps:billing.store.periodTitleUntracked', { from: dfmt(p.start), to: dfmt(p.end) })
                      : t('superadminOps:billing.store.periodTitleUnpaid', { from: dfmt(p.start), to: dfmt(p.end) })}
                  aria-label={t('superadminOps:billing.store.periodLabel', { label: p.label, status: periodStatusText(p.status) })}
                  className={`inline-flex items-center min-h-[36px] px-0.5 rounded-lg hover:opacity-80 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand transition-opacity ${p.status === 'untracked' ? 'opacity-60' : ''}`}
                >
                  <StatusChip tone={PERIOD_TONE[p.status]}>
                    {p.status === 'paid' && <Check className="w-3 h-3 mr-1" aria-hidden="true" />}
                    {p.label}
                  </StatusChip>
                </button>
              ))}
            </div>
            {amountFor && (
              <form onSubmit={submitAmount} noValidate className="mt-4 flex flex-wrap items-end gap-2" data-testid="sa-billing-period-amount">
                <SheetField
                  label={t('superadminOps:billing.store.periodAmount', { label: amountFor.period.label })}
                  htmlFor="sa-billing-period-amount"
                  className="w-44"
                >
                  <input
                    id="sa-billing-period-amount"
                    autoFocus
                    inputMode="decimal"
                    value={amountFor.value}
                    onChange={(e) => setAmountFor({ ...amountFor, value: e.target.value, error: false })}
                    aria-invalid={amountFor.error || undefined}
                    aria-describedby={amountFor.error ? 'sa-billing-period-amount-error' : undefined}
                    className={`${INPUT_CLS} tabular-nums`}
                  />
                </SheetField>
                <button type="button" className={GHOST_BTN} onClick={() => setAmountFor(null)}>{t('common:cancel')}</button>
                <button type="submit" className={GHOST_BTN}>{t('superadminOps:billing.store.periodAmountContinue')}</button>
                {amountFor.error && (
                  <p id="sa-billing-period-amount-error" role="alert" className={`${ERROR_CLS} w-full`}>{t('superadminOps:billing.store.invalidAmount')}</p>
                )}
              </form>
            )}
          </>
        )}

        {/* Πληρωμή εκτός ημερολογίου */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-[120px_170px_minmax(0,1fr)_auto] items-end gap-3" data-testid="sa-billing-new-payment">
          <SheetField label={t('superadminOps:billing.store.amount')} htmlFor="sa-billing-pay-amount">
            <input
              id="sa-billing-pay-amount"
              inputMode="decimal"
              value={newPayment.amount}
              onChange={(e) => { setPaymentError(false); setNewPayment({ ...newPayment, amount: e.target.value.replace(',', '.') }); }}
              aria-invalid={paymentError || undefined}
              aria-describedby={paymentError ? 'sa-billing-pay-amount-error' : undefined}
              className={`${INPUT_CLS} tabular-nums`}
            />
          </SheetField>
          <SheetField label={t('superadminOps:billing.store.paidAt')} htmlFor="sa-billing-pay-date">
            <input id="sa-billing-pay-date" type="date" value={newPayment.paid_at} onChange={(e) => setNewPayment({ ...newPayment, paid_at: e.target.value })} className={DATE_CLS} />
          </SheetField>
          <SheetField label={t('superadminOps:billing.store.description')} htmlFor="sa-billing-pay-desc">
            <input
              id="sa-billing-pay-desc"
              value={newPayment.period_covered}
              onChange={(e) => setNewPayment({ ...newPayment, period_covered: e.target.value })}
              placeholder={t('superadminOps:billing.store.descriptionPlaceholder')}
              className={INPUT_CLS}
            />
          </SheetField>
          <button type="button" onClick={addPayment} disabled={saving} className={GHOST_BTN}>
            {t('superadminOps:billing.store.addPayment')}
          </button>
          {paymentError && (
            <p id="sa-billing-pay-amount-error" role="alert" className={`${ERROR_CLS} sm:col-span-4 -mt-1.5`}>{t('superadminOps:billing.store.amountRequired')}</p>
          )}
        </div>

        {payments.length > 0 && (
          <ul className="mt-4 divide-y divide-line border-t border-line" aria-label={t('superadminOps:billing.store.payments')} data-testid="sa-billing-payment-list">
            {payments.map(p => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                <span className="min-w-0 text-t2">{dfmt(p.paid_at)}{p.period_covered ? ` · ${p.period_covered}` : ''}</span>
                <span className="flex items-center gap-1 shrink-0">
                  <span className="font-semibold text-t1 tabular-nums">{money(p.amount)}</span>
                  <button
                    type="button"
                    onClick={() => askDeletePayment(p)}
                    className={ICON_BTN}
                    title={t('superadminOps:billing.store.deletePaymentTitle')}
                    aria-label={t('superadminOps:billing.store.deletePaymentLabel', { amount: money(p.amount), date: dfmt(p.paid_at) })}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </SheetSection>

      {/* Hardware */}
      <SheetSection id="sa-billing-hardware" title={t('superadminOps:billing.store.hardware')} testId="sa-billing-hardware">
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_160px_96px_96px_auto] items-end gap-3" data-testid="sa-billing-new-hw">
          <SheetField label={t('superadminOps:billing.store.hwItem')} htmlFor="sa-billing-hw-item">
            <input
              id="sa-billing-hw-item"
              value={newHw.item}
              onChange={(e) => { setHwError(false); setNewHw({ ...newHw, item: e.target.value }); }}
              placeholder={t('superadminOps:billing.store.hwItemPlaceholder')}
              aria-invalid={hwError || undefined}
              aria-describedby={hwError ? 'sa-billing-hw-item-error' : undefined}
              className={INPUT_CLS}
            />
          </SheetField>
          <SheetField label={t('superadminOps:billing.store.hwSoldAt')} htmlFor="sa-billing-hw-date">
            <input id="sa-billing-hw-date" type="date" value={newHw.sold_at} onChange={(e) => setNewHw({ ...newHw, sold_at: e.target.value })} className={DATE_CLS} />
          </SheetField>
          <SheetField label={t('superadminOps:billing.store.hwPrice')} htmlFor="sa-billing-hw-price">
            <input id="sa-billing-hw-price" inputMode="decimal" value={newHw.price} onChange={(e) => setNewHw({ ...newHw, price: e.target.value.replace(',', '.') })} className={`${INPUT_CLS} tabular-nums`} />
          </SheetField>
          <SheetField label={t('superadminOps:billing.store.hwPaidAmount')} htmlFor="sa-billing-hw-paid">
            <input id="sa-billing-hw-paid" inputMode="decimal" value={newHw.paid_amount} onChange={(e) => setNewHw({ ...newHw, paid_amount: e.target.value.replace(',', '.') })} className={`${INPUT_CLS} tabular-nums`} />
          </SheetField>
          <button type="button" onClick={addHardware} disabled={saving} className={GHOST_BTN}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('superadminOps:billing.store.hwAdd')}
          </button>
          {hwError && (
            <p id="sa-billing-hw-item-error" role="alert" className={`${ERROR_CLS} sm:col-span-5 -mt-1.5`}>{t('superadminOps:billing.store.hwItemRequired')}</p>
          )}
        </div>
        {hardware.length > 0 ? (
          <ul className="divide-y divide-line mt-4 border-t border-line" aria-label={t('superadminOps:billing.store.hardware')} data-testid="sa-billing-hw-list">
            {hardware.map(h => {
              const balance = (parseFloat(h.price) || 0) - (parseFloat(h.paid_amount) || 0);
              return (
                <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-t1 truncate">{h.item}</p>
                    <p className="text-xs text-t3 tabular-nums">{dfmt(h.sold_at)} · {money(h.price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {balance > 0
                      ? <StatusChip tone="warn">{t('superadminOps:billing.store.hwBalance', { amount: money(balance) })}</StatusChip>
                      : <StatusChip tone="ok">{t('superadminOps:billing.hwSettled')}</StatusChip>}
                    <div className="w-24">
                      <input
                        defaultValue={h.paid_amount}
                        inputMode="decimal"
                        onBlur={(e) => { if (e.target.value !== String(h.paid_amount)) updateHwPaid(h, e.target.value.replace(',', '.')); }}
                        className={`${INPUT_CLS} text-right tabular-nums`}
                        title={t('superadminOps:billing.store.hwPaidTitle')}
                        aria-label={t('superadminOps:billing.store.hwPaidLabel', { item: h.item })}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => askDeleteHardware(h)}
                      className={ICON_BTN}
                      title={t('superadminOps:billing.store.hwDeleteTitle')}
                      aria-label={t('superadminOps:billing.store.hwDeleteLabel', { item: h.item })}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </SheetSection>
    </div>
  );

  const title = store.display_name || store.name;

  return (
    <DetailPanel
      open
      width="lg"
      title={title}
      subtitle={store.organization_name
        ? t('superadminOps:billing.store.subtitleOrg', { id: store.id, org: store.organization_name })
        : t('superadminOps:billing.store.subtitle', { id: store.id })}
      onClose={requestClose}
      footer={(
        <>
          <button type="button" onClick={requestClose} className={GHOST_BTN}>{t('listPage:close')}</button>
          <button
            type="button"
            onClick={saveAll} disabled={saving || loading || loadFailed}
            className={PRIMARY_BTN}
            aria-busy={saving || undefined}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {t('superadminOps:billing.store.saveAll')}
          </button>
        </>
      )}
      testId="sa-billing-store"
    >
      {body}
      <ConfirmModal
        isOpen={confirm !== null}
        variant={confirm?.variant ?? 'danger'}
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        confirmLabel={confirm?.confirmLabel}
        cancelLabel={confirm?.cancelLabel ?? t('superadminOps:back')}
        onConfirm={() => {
          const run = confirm?.run;
          setConfirm(null);
          run?.();
        }}
        onCancel={() => setConfirm(null)}
      />
    </DetailPanel>
  );
};

export default SuperAdminBillingTab;
